import {
  Bell,
  Check,
  ChevronRight,
  ChevronsUpDown,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from '@doccareplus/shared/lib';
import { cn } from '@doccareplus/shared/lib/utils';
import { usesFullBleedMobileCanvas } from './mobileShellRoute';
import { apiClientFactory } from '@doccareplus/api-client';
import { resolveMobileNav } from '@doccareplus/kernel';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import { useAppDispatch, useAppSelector } from '@doccareplus/kernel/application/store/hooks';
import {
  logout,
  selectActiveContext,
  selectMemberships,
  selectUser,
  setActiveContext,
} from '@doccareplus/kernel/application/store/slices/auth.slice';
import { toastApiError } from '@doccareplus/kernel/application';
import { NotificationList, useNotificationLiveUpdates, useUnreadNotificationCountQuery } from '@features/notifications';
import {
  getSubscriptionBannerInfo,
  hasClinicCapability,
  isSubscriptionLocked,
  SubscriptionPendingPaymentScreen,
  SubscriptionStatusBanner,
  useClinicAccessQuery,
} from '@features/clinic-access';
import { useAppointmentRequestsQuery } from '@features/scheduling/application/scheduling.query';
import { GlobalSearchSheet, type UICommand } from '@features/global-search';
import { MOBILE_BOTTOM_TAB_MIDDLE_CAPACITY, mobileNavPresentation, quickActions } from '../navModel';
import { useNavigationPageCommands, useVisibleQuickActions } from '../useSearchCommands';
import { RegisterPatientSheet } from '../../features/patients/ui/mobile/RegisterPatientSheet';
import { BookAppointmentSheet } from '../../features/scheduling/ui/BookAppointmentSheet';
import { todayDateInput } from '../../features/scheduling/domain/appointmentView';

type MobileSheet = 'clinic' | 'notifications' | 'new' | null;
type RoleSheetState = 'role' | null;

/**
 * Fire a short device vibration on primary chrome interactions. This is the
 * physical "tick" that makes premium native apps (Swiggy/Zepto/iOS) feel
 * tactile. No-ops silently on desktops and browsers without the Vibration API.
 */
function haptic(pattern: number | number[] = 8) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(pattern);
  }
}

const roleDescriptions: Record<string, string> = {
  owner: 'workspace.mobile.roleDescriptions.owner',
  doctor: 'workspace.mobile.roleDescriptions.doctor',
  receptionist: 'workspace.mobile.roleDescriptions.receptionist',
  patient: 'workspace.mobile.roleDescriptions.patient',
};

export function MobileShell() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeContext = useAppSelector(selectActiveContext);
  const accessQuery = useClinicAccessQuery();
  const memberships = useAppSelector(selectMemberships);
  const user = useAppSelector(selectUser);
  const [sheet, setSheet] = useState<MobileSheet>(null);
  const [roleSheet, setRoleSheet] = useState<RoleSheetState>(null);
  const [quickAction, setQuickAction] = useState<'patient' | 'appointment' | 'walkin' | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [signOutConfirmingMobile, setSignOutConfirmingMobile] = useState(false);
  const pageCommands = useNavigationPageCommands();
  const visibleQuickActions = useVisibleQuickActions();
  // Drives the header's elevate-on-scroll treatment: flat while at rest, frosted
  // with a hairline + shadow once content slides underneath it.
  const [scrolled, setScrolled] = useState(false);
  // Collapse the bulky identity row (clinic + bell + avatar) on scroll-down and
  // reveal it on scroll-up — the slim search bar stays put. This is the standard
  // native pattern (Swiggy/Zepto/iOS large-title) that keeps the reading surface
  // maximal while scrolling and brings chrome back the moment the user reverses.
  const [chromeCollapsed, setChromeCollapsed] = useState(false);
  const lastScrollTop = useRef(0);
  // Hysteresis state: the position where scroll direction last reversed, and the
  // current direction. We only toggle after moving a sustained distance from that
  // turning point, so the small up/down swings of a bottom rubber-band bounce
  // never cross the threshold and can't flip the header.
  const scrollPivot = useRef(0);
  const scrollDir = useRef<'up' | 'down' | null>(null);
  // Natural height of the collapsible identity row. We reserve exactly this much
  // space at the bottom of the scroll content while collapsed so the total scroll
  // height never changes — collapsing then can't shift/clamp the scroll position
  // and feed a scroll event back, which is what made the header oscillate.
  const identityRowRef = useRef<HTMLDivElement>(null);
  const [identityRowHeight, setIdentityRowHeight] = useState(0);
  const reduceMotion = useReducedMotion();
  const unreadNotifications = useUnreadNotificationCountQuery();
  useNotificationLiveUpdates();

  const { data: appointmentRequests } = useAppointmentRequestsQuery();
  const pendingRequestsCount = appointmentRequests?.filter(r => r.status === 'Requested').length ?? 0;

  useEffect(() => {
    const el = identityRowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // Observe the inner row (which keeps its natural height regardless of the
    // clipping wrapper's collapse) so the reserved space always matches.
    const observer = new ResizeObserver(() => setIdentityRowHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleShellScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    // Clamp to the real scrollable range so iOS/Android overscroll (rubber-band)
    // positions past the top/bottom don't register as direction changes.
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const y = Math.min(Math.max(0, el.scrollTop), maxScroll);
    setScrolled(y > 4);

    const delta = y - lastScrollTop.current;
    // Ignore micro-jitter; keep lastScrollTop put so tiny moves accumulate.
    if (Math.abs(delta) < 4) return;

    const dir = delta > 0 ? 'down' : 'up';
    // On a direction reversal, plant a fresh pivot at the turning point.
    if (dir !== scrollDir.current) {
      scrollPivot.current = lastScrollTop.current;
      scrollDir.current = dir;
    }
    lastScrollTop.current = y;

    if (y <= 64) {
      // Anchored near the top: always show the full chrome.
      setChromeCollapsed(false);
    } else if (dir === 'down' && y - scrollPivot.current > 36) {
      setChromeCollapsed(true);
    } else if (dir === 'up' && scrollPivot.current - y > 36) {
      setChromeCollapsed(false);
    }
  };
  const activeMembership = memberships.find((membership) => membership.clinicId === activeContext?.clinicId) ?? memberships[0];
  const clinicName = activeMembership?.clinicName ?? t('workspace.clinicName');
  const clinicInitials = getClinicInitials(clinicName);
  const userInitials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : 'U';
  const role = activeContext?.role ?? activeMembership?.role ?? '';
  const roleOptions = activeMembership?.roles?.length ? activeMembership.roles : activeMembership ? [activeMembership.role] : [];
  const matchedRole = role
    ? roleOptions.find((r) => r.toLowerCase() === role.toLowerCase()) ?? role
    : '';
  const queryClient = useQueryClient();

  // Global-search commands: navigable pages (capability-filtered) plus the quick actions, each
  // closing the search sheet and opening its bottom sheet / route.
  const searchCommands: UICommand[] = [
    ...pageCommands,
    ...visibleQuickActions.map((action) => {
      const Icon = action.icon;
      return {
        id: `action:${(action as { actionType?: string }).actionType ?? (action as { route?: string }).route}`,
        category: 'actions' as const,
        title: t(action.labelKey),
        subtitle: t(action.descriptionKey),
        icon: <Icon className="h-[17px] w-[17px]" />,
        run: () => {
          setSearchOpen(false);
          const actionType = (action as { actionType?: 'patient' | 'appointment' | 'walkin' }).actionType;
          if (actionType) setQuickAction(actionType);
          else navigate((action as { route: string }).route);
        },
      };
    }),
  ];

  const isFrontDesk = matchedRole.toLowerCase() === 'receptionist' || matchedRole.toLowerCase() === 'owner';
  // Smart bottom bar: resolve which 3 middle destinations this role/permission set can actually reach,
  // in priority order — when a role lacks permission for a higher-priority one (e.g. billing staff
  // without queue access), the next reachable destination is promoted into its slot instead of
  // leaving a dead-end tab. Home and More are always the first/last slot.
  const { bottomTabPaths } = resolveMobileNav(isFrontDesk, accessQuery.data, MOBILE_BOTTOM_TAB_MIDDLE_CAPACITY);
  const primaryRoutes = ['/dashboard', ...bottomTabPaths, '/mobile/more'].map((path) => {
    const presentation = mobileNavPresentation[path];
    return {
      path,
      labelKey: presentation.labelKey,
      icon: presentation.icon,
      badge: path === '/appointments/requests' ? pendingRequestsCount : undefined,
    };
  });
  // Bottom-tab destinations show full shell chrome; everything else is a drill-down/hub page that
  // owns its own back button, so the shell hides its heavy header, search bar, and "new" FAB.
  // The bottom navigation remains visible everywhere, with no active tab on secondary routes.
  const immersiveChrome = !primaryRoutes.some((route) => route.path === pathname);
  // Narrower than `immersiveChrome`: only the handful of pages that render their own edge-to-edge
  // sticky app-bar need zero shell padding. Every other secondary route keeps `.page-wrap-mobile` for
  // its own (non-sticky) in-page back button and content.
  const fullBleedCanvas = usesFullBleedMobileCanvas(pathname);

  const handleClinicChange = async (clinicId: string) => {
    const targetMembership = memberships.find((membership) => membership.clinicId === clinicId);
    if (!targetMembership) return;

    try {
      const result = await authApi.switchContext(targetMembership.clinicId, targetMembership.role);
      // Set headers synchronously so the immediate clinic-access refetch uses the new context (not the stale one).
      apiClientFactory.addHeader('X-Clinic-Id', result.clinicId);
      apiClientFactory.addHeader('X-Active-Role', result.role);
      await queryClient.cancelQueries({ queryKey: ['clinic-access'] });
      dispatch(setActiveContext({ clinicId: result.clinicId, clinicName: result.clinicName, role: result.role }));
      localStorage.setItem('doccareplus.current_clinic', result.clinicId);
      localStorage.setItem('doccareplus.current_role', result.role);
      queryClient.removeQueries({ queryKey: ['clinic-access'] });
      navigate(result.defaultRoute, { replace: true });
      setSheet(null);
    } catch (error) {
      toastApiError(error, t('errors.switchClinicFailed'));
    }
  };

  const handleRoleChange = async (nextRole: string) => {
    if (!activeMembership) return;

    try {
      const result = await authApi.switchContext(activeMembership.clinicId, nextRole);
      apiClientFactory.addHeader('X-Clinic-Id', result.clinicId);
      apiClientFactory.addHeader('X-Active-Role', result.role);
      await queryClient.cancelQueries({ queryKey: ['clinic-access'] });
      dispatch(setActiveContext({ clinicId: result.clinicId, clinicName: result.clinicName, role: result.role }));
      localStorage.setItem('doccareplus.current_clinic', result.clinicId);
      localStorage.setItem('doccareplus.current_role', result.role);
      queryClient.removeQueries({ queryKey: ['clinic-access'] });
      navigate(result.defaultRoute, { replace: true });
      setRoleSheet(null);
    } catch (error) {
      toastApiError(error, t('errors.switchRoleFailed'));
    }
  };

  // While the clinic is PendingPayment, the workspace is fully locked: no header identity row, no
  // bottom nav, no Outlet — otherwise a direct URL to e.g. /patients would still render underneath.
  // Only sign-out stays reachable so the clinic isn't stuck if the wrong account signed in.
  if (isSubscriptionLocked(accessQuery.data?.subscriptionStatus)) {
    return (
      <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--text)] md:hidden">
        <div className="mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col overflow-hidden bg-[var(--bg)]">
          <header
            className="flex items-center justify-between px-4 py-3"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
          >
            <div className="flex min-w-0 items-center gap-2">
              <img src="/short_logo.svg" alt="Logo" className="h-6 w-6 shrink-0" />
              <span className="truncate text-[13.5px] font-bold text-[var(--text)]">{clinicName}</span>
            </div>
            <button
              type="button"
              onClick={() => setSignOutConfirmingMobile(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-[9px] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--danger)]"
            >
              {t('actions.signOut')}
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain page-wrap-mobile">
            <SubscriptionPendingPaymentScreen
              onCheckAgain={() => accessQuery.refetch()}
              isChecking={accessQuery.isFetching}
              planName={accessQuery.data?.planName}
              currency={accessQuery.data?.currency}
              finalPrice={accessQuery.data?.finalPrice}
            />
          </div>
          {signOutConfirmingMobile && (
            <MobileSheetFrame title={t('actions.signOutConfirmTitle')} onClose={() => setSignOutConfirmingMobile(false)}>
              <p className="mb-4 text-[13.5px] text-[var(--text-2)]">{t('actions.signOutConfirmDescription')}</p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => dispatch(logout())}
                  className="flex h-11 items-center justify-center rounded-[12px] bg-[var(--danger)] text-[13.5px] font-semibold text-white"
                >
                  {t('actions.signOut')}
                </button>
                <button
                  type="button"
                  onClick={() => setSignOutConfirmingMobile(false)}
                  className="flex h-11 items-center justify-center rounded-[12px] border border-[var(--border)] text-[13.5px] font-semibold text-[var(--text)]"
                >
                  {t('actions.staySignedIn')}
                </button>
              </div>
            </MobileSheetFrame>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--text)] md:hidden">
      <div className="mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col overflow-hidden bg-[var(--bg)]">
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          onScroll={handleShellScroll}
        >
          <header
            aria-label={t('workspace.mobile.headerLabel')}
            className={cn(
              'sticky top-0 z-30 px-4 pb-2.5 transition-shadow duration-200',
              'bg-[color-mix(in_srgb,var(--surface)_80%,transparent)] backdrop-blur-xl',
              immersiveChrome && 'hidden',
              scrolled
                ? 'border-b border-[var(--border)] shadow-[var(--shadow-2)]'
                : 'border-b border-transparent'
            )}
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
          >
            <motion.div
              initial={false}
              animate={{
                height: chromeCollapsed ? 0 : 'auto',
                opacity: chromeCollapsed ? 0 : 1,
              }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }
              }
              className="overflow-hidden"
              inert={chromeCollapsed}
            >
              <div ref={identityRowRef} className="flex min-w-0 items-center gap-2">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  aria-label={t('workspace.mobile.switchClinic')}
                  onClick={() => {
                    haptic();
                    setSheet('clinic');
                  }}
                  className="flex min-w-0 flex-1 items-center gap-[9px] rounded-[12px] p-0 text-left text-[var(--text)] no-underline hover:no-underline"
                >
                  <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-gradient-to-br from-[var(--brand-500)] to-[var(--brand-700)] text-[13px] font-bold text-white shadow-[var(--shadow-1)]">
                    {clinicInitials}
                  </span>
                  <span className="flex min-w-0 max-w-full items-center gap-[5px]">
                    <span className="min-w-0 max-w-full">
                      <span className="block max-w-full truncate text-[14.5px] font-bold leading-tight text-[var(--text)]">{clinicName}</span>
                      <span className="mt-0.5 block max-w-full truncate text-[11.5px] font-medium leading-tight text-[var(--text-3)]">
                        {t('workspace.mobile.branch')}
                      </span>
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[var(--text-3)]" aria-hidden="true" />
                  </span>
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.92 }}
                  type="button"
                  aria-label={t('notifications')}
                  onClick={() => {
                    haptic();
                    setSheet('notifications');
                  }}
                  className="relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] shadow-[var(--shadow-1)]"
                >
                  <Bell className="h-[17px] w-[17px]" aria-hidden="true" />
                  {(unreadNotifications.data?.count ?? 0) > 0 && <span className="absolute right-[8px] top-[8px] h-2 w-2 rounded-full border-2 border-[var(--surface)] bg-[var(--danger-solid)]" />}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.92 }}
                  type="button"
                  aria-label={t('workspace.mobile.switchRoleLabel')}
                  onClick={() => {
                    haptic();
                    setRoleSheet('role');
                  }}
                  className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] border-none bg-[var(--surface-3)] text-[11.5px] font-bold text-[var(--text)]"
                >
                  {userInitials}
                </motion.button>
              </div>
            </motion.div>

            <motion.button
              whileTap={{ scale: 0.985 }}
              type="button"
              aria-label={t('search.open')}
              onClick={() => {
                haptic();
                setSearchOpen(true);
              }}
              className="mt-2.5 flex h-11 w-full items-center gap-2.5 rounded-[14px] border border-transparent bg-[var(--surface-2)] px-3.5 text-left text-[var(--text-3)]"
            >
              <Search className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate text-[13.5px] font-medium">{t('placeholder.search')}</span>
            </motion.button>
          </header>

          {!immersiveChrome && (
            <SubscriptionStatusBanner
              info={getSubscriptionBannerInfo(accessQuery.data)}
              canManageSubscription={hasClinicCapability(accessQuery.data, ['clinic.settings.manage'])}
            />
          )}

          <main className={fullBleedCanvas ? 'min-h-full p-0' : 'page-wrap-mobile'}>
            <Outlet />
          </main>
          {/* Reserve the reclaimed identity-row height at the bottom while collapsed
            so the scroll content's total height stays constant. This keeps the
            scroll position stable across a collapse — no clamp, no feedback loop. */}
          <motion.div
            aria-hidden
            initial={false}
            animate={{ height: chromeCollapsed ? identityRowHeight : 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }}
          />
        </div>

        <motion.button
          initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 520, damping: 30, delay: 0.05 }}
          whileTap={{ scale: 0.88 }}
          type="button"
          aria-label={t('new')}
          onClick={() => {
            haptic([10, 8, 10]);
            setSheet('new');
          }}
          className={cn(
            'fixed right-[max(18px,calc(50%-195px))] z-30 flex h-[58px] w-[58px] items-center justify-center rounded-[19px] border-none bg-[var(--primary)] text-[var(--primary-fg)] shadow-[0_10px_24px_rgba(5,150,105,.42)]',
            immersiveChrome && 'hidden',
          )}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 92px)' }}
        >
          <Plus className="h-7 w-7" aria-hidden="true" />
        </motion.button>

        <nav
          aria-label={t('workspace.mobile.navigationLabel')}
          className="flex items-stretch border-t border-[var(--border)] bg-[var(--surface)] px-1.5 pt-1.5"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
        >
          {primaryRoutes.map((route) => {
            const Icon = route.icon;

            return (
              <NavLink
                key={route.path}
                to={route.path}
                end
                onClick={() => haptic()}
                className={({ isActive }) =>
                  cn(
                    'flex min-w-0 flex-1 flex-col items-center gap-1 bg-transparent py-1 text-[10.5px] font-medium text-[var(--text-3)] no-underline hover:no-underline',
                    isActive && 'font-bold text-[var(--primary)]'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <motion.span whileTap={{ scale: 0.82 }} className="relative flex items-center justify-center">
                      <Icon
                        className="h-[23px] w-[23px]"
                        strokeWidth={isActive ? 2.3 : 2}
                        aria-hidden="true"
                      />
                      {route.badge && route.badge > 0 ? (
                        <span className="absolute -right-2 -top-1 flex min-w-[16px] items-center justify-center rounded-[20px] border-2 border-[var(--surface)] bg-[var(--danger-solid)] px-1 py-0 text-[10px] font-bold text-white shadow-sm box-content">
                          {route.badge}
                        </span>
                      ) : null}
                    </motion.span>
                    <span className="truncate">{t(route.labelKey, { defaultValue: route.labelKey.split('.').pop() })}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <AnimatePresence>
          {sheet === 'clinic' && (
            <MobileSheetFrame key="clinic" title={t('workspace.mobile.clinicSwitcherTitle')} onClose={() => setSheet(null)}>
              <div className="flex flex-col gap-[10px]">
                {memberships.map((membership) => {
                  const isActive = membership.clinicId === activeContext?.clinicId;

                  return (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      key={membership.clinicId}
                      type="button"
                      onClick={() => handleClinicChange(membership.clinicId)}
                      className={cn(
                        'flex min-h-[64px] items-center gap-3.5 rounded-[14px] border px-3.5 py-3 text-left',
                        isActive
                          ? 'border-[var(--primary)] bg-[var(--primary-weak)] text-[var(--primary)]'
                          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
                      )}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-gradient-to-br from-[var(--brand-500)] to-[var(--brand-700)] text-[13px] font-bold text-white">
                        {getClinicInitials(membership.clinicName)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-bold leading-tight text-[var(--text)]">{membership.clinicName}</span>
                        <span className="mt-0.5 block truncate text-[12.5px] font-medium leading-tight text-[var(--text-3)]">
                          {t(`workspace.mobile.clinicMeta.${membership.clinicId}`, { defaultValue: t('workspace.mobile.clinicMeta.default') })}
                        </span>
                      </span>
                      {isActive && <Check className="h-[18px] w-[18px] shrink-0 text-[var(--primary)]" aria-hidden="true" />}
                    </motion.button>
                  );
                })}
                {memberships.length === 1 && (
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="flex min-h-[56px] items-center gap-3.5 rounded-[14px] border border-dashed border-[var(--border-strong)] bg-[var(--surface)] px-3.5 py-3 text-left text-[var(--text)]"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[var(--surface-2)] text-[var(--text-2)]">
                      <Plus className="h-[18px] w-[18px]" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-bold">{t('actions.createClinic', { defaultValue: 'Create new clinic' })}</span>
                      <span className="mt-0.5 block truncate text-[12.5px] font-medium text-[var(--text-3)]">
                        {t('workspace.mobile.createClinicHint')}
                      </span>
                    </span>
                  </motion.button>
                )}
              </div>
            </MobileSheetFrame>
          )}

          {sheet === 'notifications' && (
            <MobileSheetFrame key="notifications" title={t('notifications')} onClose={() => setSheet(null)}>
              <NotificationList onNotificationSelected={() => setSheet(null)} showMarkAll />
            </MobileSheetFrame>
          )}

          {sheet === 'new' && (
            <MobileSheetFrame key="new" title={t('workspace.mobile.quickActionsTitle')} onClose={() => setSheet(null)}>
              <div className="flex flex-col gap-[10px]">
                {quickActions.map((action) => {
                  const Icon = action.icon;

                  return (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      key={action.labelKey}
                      type="button"
                      onClick={() => {
                        setSheet(null);
                        if (action.actionType) {
                          setQuickAction(action.actionType);
                        } else {
                          navigate(action.route, { state: action.state });
                        }
                      }}
                      className="flex min-h-[64px] items-center gap-3.5 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-3.5 py-3 text-left text-[var(--text)]"
                    >
                      <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px]', getActionToneClass(action.tone))}>
                        <Icon className="h-[21px] w-[21px]" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-bold leading-tight">{t(action.labelKey)}</span>
                        <span className="mt-0.5 block truncate text-[12.5px] font-medium leading-tight text-[var(--text-3)]">
                          {t(action.descriptionKey)}
                        </span>
                      </span>
                      <ChevronRight className="h-[18px] w-[18px] shrink-0 text-[var(--text-3)]" aria-hidden="true" />
                    </motion.button>
                  );
                })}
              </div>
            </MobileSheetFrame>
          )}

          {roleSheet === 'role' && (
            <MobileRoleSwitcherSheet
              key="role"
              roleOptions={roleOptions}
              activeRole={matchedRole}
              userInitials={userInitials}
              onRoleChange={handleRoleChange}
              onClose={() => setRoleSheet(null)}
            />
          )}
        </AnimatePresence>

        {quickAction === 'patient' && (
          <RegisterPatientSheet onClose={() => setQuickAction(null)} onRegistered={(id) => { setQuickAction(null); navigate(`/patients/${id}`); }} />
        )}
        {quickAction === 'appointment' && (
          <BookAppointmentSheet isOpen={true} onClose={() => setQuickAction(null)} defaultDate={''} />
        )}
        {quickAction === 'walkin' && (
          <BookAppointmentSheet isOpen={true} onClose={() => setQuickAction(null)} defaultMode="walkin" defaultDate={todayDateInput()} />
        )}

        <GlobalSearchSheet open={searchOpen} onClose={() => setSearchOpen(false)} commands={searchCommands} />

      </div>
    </div>
  );
}

function getClinicInitials(name: string) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return initials || 'C';
}

function getRoleInitials(roleName: string, fallback: string) {
  return roleName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || fallback;
}

function getActionToneClass(tone: (typeof quickActions)[number]['tone']) {
  if (tone === 'info') return 'bg-[var(--info-bg)] text-[var(--info-solid)]';
  if (tone === 'warning') return 'bg-[var(--warning-bg)] text-[var(--warning-solid)]';

  return 'bg-[var(--primary-weak)] text-[var(--primary)]';
}

export function MobileSheetFrame({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  const reduceMotion = useReducedMotion();

  return (
    <div className="fixed inset-0 z-40 mx-auto max-w-[430px]">
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        type="button"
        aria-label={t('actions.close')}
        className="absolute inset-0 w-full bg-[rgba(11,16,14,.55)] backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.section
        initial={reduceMotion ? { opacity: 0 } : { y: '100%' }}
        animate={reduceMotion ? { opacity: 1 } : { y: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { y: '100%' }}
        transition={
          reduceMotion
            ? { duration: 0.15 }
            : { type: 'spring', stiffness: 420, damping: 40, mass: 0.9 }
        }
        drag={reduceMotion ? false : 'y'}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.55 }}
        onDragEnd={(_event, info) => {
          if (info.offset.y > 120 || info.velocity.y > 600) onClose();
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute inset-x-0 bottom-0 rounded-t-[28px] border border-[var(--border)] bg-[var(--surface)] px-6 shadow-[var(--shadow-3)]"
        style={{ paddingTop: '12px', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)' }}
      >
        <div className="mx-auto mb-4 h-[5px] w-10 rounded-full bg-[var(--border-strong)]" />
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0 pt-0.5">
            <h2 className="truncate text-[18px] font-bold leading-tight text-[var(--text)]">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[13px] font-medium leading-tight text-[var(--text-3)]">{subtitle}</p>}
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            type="button"
            aria-label={t('actions.close')}
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] border-none bg-[var(--surface-2)] text-[var(--text-2)]"
          >
            <X className="h-[18px] w-[18px]" aria-hidden="true" />
          </motion.button>
        </div>
        {children}
      </motion.section>
    </div>
  );
}

export function MobileRoleSwitcherSheet({
  roleOptions,
  activeRole,
  userInitials,
  onRoleChange,
  onClose,
}: {
  roleOptions: string[];
  activeRole: string;
  userInitials: string;
  onRoleChange: (role: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');

  return (
    <MobileSheetFrame title={t('viewAs')} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        {roleOptions.map((option) => {
          const active = option === activeRole;
          const normalizedRole = option.toLowerCase();

          return (
            <motion.button
              whileTap={{ scale: 0.98 }}
              key={option}
              type="button"
              aria-label={t(`role.${normalizedRole}`, { defaultValue: option })}
              onClick={() => onRoleChange(option)}
              className={cn(
                'flex min-h-[64px] items-center gap-3.5 rounded-[14px] border px-3.5 py-3 text-left',
                active
                  ? 'border-[var(--primary)] bg-[var(--primary-weak)] text-[var(--primary)]'
                  : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
              )}
            >
              <span
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[13px] font-bold',
                  active ? 'bg-[var(--primary)] text-[var(--primary-fg)]' : 'bg-[var(--surface-3)] text-[var(--text-2)]'
                )}
              >
                {getRoleInitials(option, userInitials)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold leading-tight text-[var(--text)]">
                  {t(`role.${normalizedRole}`, { defaultValue: option })}
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] font-medium leading-tight text-[var(--text-3)]">
                  {t(roleDescriptions[normalizedRole] ?? 'workspace.mobile.roleDescriptions.default')}
                </span>
              </span>
              {active && <Check className="h-[18px] w-[18px] shrink-0 text-[var(--primary)]" aria-hidden="true" />}
            </motion.button>
          );
        })}
      </div>
    </MobileSheetFrame>
  );
}
